import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, PauseCircle, History, Loader2, Tag } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import { UcParaRateio } from '../../../src/hooks/useRateioBalanceamento';
import { pausarUcManual } from './rateioTravamentoActions';
import UcHistoricoPopover from './UcHistoricoPopover';
import GdModalidadeModal from './GdModalidadeModal';

interface UcAcoesMenuProps {
  uc: UcParaRateio;
  onChanged: () => void;
  disabled?: boolean;
}

const MENU_W = 190;

/** Kebab de ações da linha da UC (Seção 11 da spec): "Pausar UC" e "Ver Histórico". */
const UcAcoesMenu: React.FC<UcAcoesMenuProps> = ({ uc, onChanged, disabled = false }) => {
  const { addToast } = useToast();
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [pausando, setPausando] = useState(false);
  const [historicoAnchor, setHistoricoAnchor] = useState<DOMRect | null>(null);
  const [showGdModal, setShowGdModal] = useState(false);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const anchor = btnRef.current.getBoundingClientRect();
    const margin = 8;
    let left = anchor.right - MENU_W;
    if (left < margin) left = margin;
    setPos({ top: anchor.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDocClick);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handlePausar = async () => {
    setPausando(true);
    try {
      await pausarUcManual(uc.id);
      addToast('UC pausada.', 'success');
      setOpen(false);
      onChanged();
    } catch (error: any) {
      console.error('Error pausing UC', error);
      addToast(error?.message || 'Erro ao pausar a UC.', 'error');
    } finally {
      setPausando(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        title="Ações da UC"
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <MoreVertical size={16} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: pos.top, left: pos.left, width: MENU_W }}
            className="fixed z-[2000] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 py-1 animate-fade-in"
          >
            <button
              type="button"
              disabled={pausando}
              onClick={handlePausar}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
            >
              {pausando ? <Loader2 size={14} className="animate-spin" /> : <PauseCircle size={14} />}
              Pausar UC
            </button>
            <button
              type="button"
              onClick={e => {
                setHistoricoAnchor((e.currentTarget as HTMLElement).getBoundingClientRect());
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <History size={14} />
              Ver Histórico
            </button>
            <button
              type="button"
              onClick={() => {
                setShowGdModal(true);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Tag size={14} />
              Definir GD I/II
            </button>
          </div>,
          document.body
        )}

      {historicoAnchor && <UcHistoricoPopover anchor={historicoAnchor} onClose={() => setHistoricoAnchor(null)} uc={uc} />}
      {showGdModal && (
        <GdModalidadeModal
          uc={uc}
          onClose={() => setShowGdModal(false)}
          onSaved={() => {
            setShowGdModal(false);
            onChanged();
          }}
        />
      )}
    </>
  );
};

export default UcAcoesMenu;
